import type { LegalRun, ParagraphBlock } from '../../EdgeDocsCore/models/ast';
import type { LineBox } from '../../EdgeDocsCore/models/layout';
import { getStylesRegistry } from '../styles/registry';
import { resolveRunStyle } from '../styles/resolveStyle';
import { createMeasurementHost } from '../measurement/MeasurementHost';
import { measureRuns, type RunWithStyle } from '../measurement/TextMeasurer';

export interface ParagraphLayoutOptions {
  widthPx: number;
}

export interface ParagraphLayoutResult {
  lineBoxes: LineBox[];
  heightPx: number;
  graphemeMap: Record<string, { lineIndex: number; x: number }>;
}

export async function layoutParagraph(block: ParagraphBlock, opts: ParagraphLayoutOptions): Promise<ParagraphLayoutResult> {
  const runs = (block.children || []) as LegalRun[];
  if (runs.length === 0) {
    return { lineBoxes: [], heightPx: 0, graphemeMap: {} };
  }
  const host = createMeasurementHost();
  await host.start();
  const container = host.getContainer();
  container.style.width = `${Math.max(1, Math.round(opts.widthPx))}px`;

  const registry = getStylesRegistry();
  const paragraphStyle = block.styleId ? registry.paragraphStyles[block.styleId] : undefined;

  // Resolve styles to ensure font weight/italic are applied in measurement (subset in TextMeasurer)
  const resolved: RunWithStyle[] = runs.map((r) => ({ run: r, style: resolveRunStyle(paragraphStyle, r) }));
  const measure = measureRuns(container, resolved, { granularity: 'full' });

  // Build accurate LineBox[] from graphemeMap by grouping graphemes per line
  const lineBoxes: LineBox[] = [];
  const byLine: Record<number, { start?: { runId: string; g: number }; end?: { runId: string; g: number } }> = {};
  for (const [key, val] of Object.entries(measure.graphemeMap)) {
    const [runId, gStr] = key.split(':');
    const g = Number(gStr);
    const line = val.lineIndex;
    const group = (byLine[line] ||= {});
    if (group.start === undefined || g < (group.start?.g ?? Number.POSITIVE_INFINITY)) group.start = { runId, g };
    if (group.end === undefined || g > (group.end?.g ?? Number.NEGATIVE_INFINITY)) group.end = { runId, g };
  }
  measure.rectsByLine.forEach((r, idx) => {
    const group = byLine[idx] || {};
    const start = group.start || { runId: runs[0]?.id || `${block.id}:run0`, g: 0 };
    const end = group.end || start;
    lineBoxes.push({
      blockId: block.id,
      runId: start.runId,
      lineIndexInBlock: idx,
      startGrapheme: start.g,
      endGrapheme: end.g,
      x: r.x,
      y: r.y,
      width: r.width,
      height: r.height,
      baseline: r.baseline,
    });
  });

  // Prefer style-driven line height for vertical rhythm when possible
  const firstResolved = resolved[0]?.style;
  const lineHeightMultiple = firstResolved?.lineHeight ?? 1.5;
  const fontSizePx = Math.max(1, (firstResolved?.fontSizePt ?? 12) * (96 / 72));
  const lineHeightPx = lineHeightMultiple * fontSizePx;
  const heightPx = Math.max(lineBoxes.length * lineHeightPx, lineBoxes.reduce((h, b) => Math.max(h, b.y + b.height), 0));
  await host.teardown();
  return { lineBoxes, heightPx, graphemeMap: measure.graphemeMap };
}


